import type { ReactElement } from "react";
import { memo, useCallback, useMemo } from "react";

import useLatestValue from "app/custom-hooks/useLatestValue";
import { FilterItemCard } from "app/pages/sales-intelligence/common-components/filters-panel/components/FilterItemCard/FilterItemCard";
import { FilterItemClearButton } from "app/pages/sales-intelligence/common-components/filters-panel/components/FilterItemClearButton/FilterItemClearButton";
import { FilterItemUpgradeIcon } from "app/pages/sales-intelligence/common-components/filters-panel/components/FilterItemUpgradeIcon/FilterItemUpgradeIcon";
import type { FilterInstanceType } from "app/pages/sales-intelligence/modules/advanced-search-filtering/types/filterInstance";
import type { IPlainObject } from "app/pages/sales-intelligence/types/plainObject";

import type { IFilterItemCardActionsBaseProps, IFilterItemCardActionsProps } from "./types";

interface IProps<T extends IPlainObject> extends Omit<IFilterItemCardActionsBaseProps, "onClear" | "onToggleExpand"> {
    filterConfig: FilterInstanceType<T, unknown, unknown>;
    filterDtoValue: T[keyof T] | undefined;
    isDisabled?: boolean;
    onFilterUpdate: (key: keyof T, value?: T[keyof T]) => void;
    onClear: (key: keyof T) => void;
    onToggleExpand: (key: keyof T) => void;
    renderActions?: (props: IFilterItemCardActionsProps<T>) => ReactElement;
}

const FilterPanelItemBase = <T extends IPlainObject>({
    isExpanded,
    isShowClearButton,
    isDisabled,
    filterConfig,
    filterDtoValue,
    renderActions,
    onToggleExpand,
    onFilterUpdate,
    onClear,
}: IProps<T>) => {
    const onFilterUpdateRef = useLatestValue(onFilterUpdate);

    const {
        key,
        title,
        tooltipText,
        disabledTooltip,
        filter,
        gateStatus,
        filterView: FilterView,
        filterResultView: FilterResultView,
        filterCardView: FilterCardView = FilterItemCard,
        icon: FilterIcon,
        tag: FilterTag,
        hideTitleInPanel,
    } = filterConfig;

    const GateView = gateStatus?.view;
    const isCardExpandable = !!FilterView || !!gateStatus;

    const handleFilterUpdate = useCallback((value?: unknown): void => {
        const apiValue = filter.toApiValue(value);

        if (filter.isValueEmpty(apiValue)) {
            onFilterUpdateRef.current(key, undefined);
            return;
        }

        onFilterUpdateRef.current(key, apiValue);
    }, []);

    const handleClear = () => onClear(key);
    const handleToggleExpand = () => onToggleExpand(key);

    const currentFilterValue = useMemo(() => filter.fromApiValue(filterDtoValue), [filter, filterDtoValue]);

    const filterContent = useMemo(() => {
        if (GateView) {
            return <GateView />;
        }

        if (FilterView) {
            return (
                <FilterView
                    filter={filter}
                    value={currentFilterValue}
                    isExpanded={isExpanded}
                    isDisabled={isDisabled}
                    onFilterUpdate={handleFilterUpdate}
                />
            );
        }

        return null;
    }, [GateView, FilterView, filter, currentFilterValue, isExpanded, isDisabled, handleFilterUpdate]);

    const renderItemAction = (): ReactElement | null => {
        if (gateStatus?.type === "upgrade") {
            return <FilterItemUpgradeIcon />;
        }

        if (renderActions) {
            return renderActions({
                key,
                isCardExpandable,
                isExpanded,
                isShowClearButton,
                onClear: handleClear,
                onToggleExpand: handleToggleExpand,
            });
        }

        return isShowClearButton ? <FilterItemClearButton onClick={handleClear} /> : null;
    };

    const filterResultView = FilterResultView ? (
        <FilterResultView
            filter={filter}
            value={currentFilterValue}
            isExpanded={isExpanded}
            isDisabled={isDisabled}
            onFilterUpdate={handleFilterUpdate}
        />
    ) : null;

    return (
        <FilterCardView
            dataAutomation={`filter-panel.item-container.${String(key)}`}
            title={hideTitleInPanel ? undefined : title}
            tooltipText={tooltipText}
            isDisabled={isDisabled}
            disabledTooltip={disabledTooltip}
            isExpanded={isExpanded}
            filterResultView={filterResultView}
            renderActions={renderItemAction}
            icon={FilterIcon ? <FilterIcon /> : null}
            tag={FilterTag ? <FilterTag /> : null}
            onToggleExpand={handleToggleExpand}
        >
            {isCardExpandable ? filterContent : null}
        </FilterCardView>
    );
};

// Memoized so a config rebuild that changes only some cards' props does
// not re-render the unchanged cards
export const FilterPanelItem = memo(FilterPanelItemBase) as typeof FilterPanelItemBase;
